import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

// Fix Leaflet's default icon path issue with bundlers
// The icons are served from /leaflet/ in the public folder
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "/leaflet/marker-icon-2x.png",
  iconUrl: "/leaflet/marker-icon.png",
  shadowUrl: "/leaflet/marker-shadow.png"
})

/**
 * Cellar Map Controller
 *
 * Displays bottles on an interactive Leaflet map with custom markers.
 * Each marker shows the bottle's image (or emoji) and rating.
 * Clicking a marker navigates to the bottle's show page.
 *
 * @example HTML structure
 *   <div data-controller="cellar-map"
 *        data-cellar-map-bottles-value='[{"id":1,"lat":44.8,"lng":-0.5,...}]'>
 *     <div data-cellar-map-target="container" class="h-96"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["container"]
  static values = {
    bottles: Array,
    tileUrl: { type: String, default: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" },
    attribution: { type: String, default: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' }
  }

  connect() {
    // Find the parent panel (if inside tabs)
    this.panel = this.element.closest("[data-tabs-target='panel']")
    
    // Listen for tab shown event on the panel to initialize map when visible
    if (this.panel) {
      this.boundInitializeMap = this.initializeMap.bind(this)
      this.panel.addEventListener("tab:shown", this.boundInitializeMap)
      
      // If panel is already visible, initialize immediately
      if (!this.panel.classList.contains("hidden")) {
        this.initializeMap()
      }
    } else {
      // Not in tabs, initialize immediately
      this.initializeMap()
    }
  }

  disconnect() {
    // Clean up event listener
    if (this.panel && this.boundInitializeMap) {
      this.panel.removeEventListener("tab:shown", this.boundInitializeMap)
    }
    
    // Clean up map
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  /**
   * Initializes the Leaflet map with bottle markers.
   * Only runs once, even if called multiple times.
   */
  initializeMap() {
    if (this.map) return // Already initialized
    if (!this.hasContainerTarget) return

    // Filter bottles with valid coordinates
    const geocodedBottles = this.bottlesValue.filter(b => b.lat && b.lng)
    
    if (geocodedBottles.length === 0) {
      this.showEmptyState()
      return
    }

    // Create map centered on first bottle
    this.map = L.map(this.containerTarget, {
      scrollWheelZoom: true,
      zoomControl: true
    })

    // Add OpenStreetMap tiles
    L.tileLayer(this.tileUrlValue, {
      attribution: this.attributionValue,
      maxZoom: 18
    }).addTo(this.map)

    // Add markers for each bottle
    const markers = geocodedBottles.map(bottle => this.createMarker(bottle))
    
    // Fit map to show all markers
    if (markers.length > 0) {
      const group = L.featureGroup(markers)
      this.map.fitBounds(group.getBounds().pad(0.1))
    }

    // Limit max zoom when fitting bounds
    if (this.map.getZoom() > 12) {
      this.map.setZoom(12)
    }
  }

  /**
   * Creates a custom marker for a bottle with image and rating.
   *
   * @param {Object} bottle - The bottle data
   * @returns {L.Marker} The Leaflet marker
   */
  createMarker(bottle) {
    const icon = this.createCustomIcon(bottle)
    
    const marker = L.marker([bottle.lat, bottle.lng], { icon })
      .addTo(this.map)
    
    // Add popup with bottle info
    const popupContent = this.createPopupContent(bottle)
    marker.bindPopup(popupContent, {
      maxWidth: 200,
      className: "bottle-popup"
    })

    // Navigate to bottle on click (after popup closes)
    marker.on("popupopen", () => {
      const link = marker.getPopup().getElement().querySelector(".bottle-link")
      if (link) {
        link.addEventListener("click", (e) => {
          e.preventDefault()
          window.location.href = bottle.url
        })
      }
    })

    return marker
  }

  /**
   * Creates a custom div icon with bottle image and rating.
   *
   * @param {Object} bottle - The bottle data
   * @returns {L.DivIcon} The custom Leaflet icon
   */
  createCustomIcon(bottle) {
    const rating = bottle.rating ? bottle.rating.toFixed(1) : "-"
    const ratingColor = this.getRatingColor(bottle.rating)
    
    let imageHtml
    if (bottle.imageUrl) {
      imageHtml = `<img src="${bottle.imageUrl}" alt="${bottle.name}" class="w-full h-full object-cover" />`
    } else {
      imageHtml = `<span class="text-2xl">${bottle.emoji || "🍷"}</span>`
    }

    const html = `
      <div class="bottle-marker">
        <div class="bottle-marker-image">
          ${imageHtml}
        </div>
        <div class="bottle-marker-rating" style="background-color: ${ratingColor}">
          ${rating}
        </div>
      </div>
    `

    return L.divIcon({
      html: html,
      className: "bottle-marker-container",
      iconSize: [50, 65],
      iconAnchor: [25, 65],
      popupAnchor: [0, -65]
    })
  }

  /**
   * Creates the popup content HTML for a bottle.
   *
   * @param {Object} bottle - The bottle data
   * @returns {string} HTML string for the popup
   */
  createPopupContent(bottle) {
    const rating = bottle.rating ? bottle.rating.toFixed(1) : null
    const year = bottle.year ? `(${bottle.year})` : ""
    const ratingColor = this.getRatingColor(bottle.rating)
    
    // Build image HTML if available
    let imageHtml = ""
    if (bottle.imageUrl) {
      imageHtml = `
        <div style="width: 60px; height: 60px; margin: 0 auto 8px; border-radius: 8px; overflow: hidden; border: 2px solid #e5e7eb;">
          <img src="${bottle.imageUrl}" alt="${bottle.name}" style="width: 100%; height: 100%; object-fit: cover;" />
        </div>
      `
    } else {
      imageHtml = `
        <div style="width: 60px; height: 60px; margin: 0 auto 8px; border-radius: 8px; background: #f3f4f6; display: flex; align-items: center; justify-content: center; font-size: 28px;">
          ${bottle.emoji || "🍷"}
        </div>
      `
    }
    
    // Build rating badge if available
    let ratingHtml = ""
    if (rating) {
      ratingHtml = `
        <div style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 12px; background: ${ratingColor}; color: white; font-size: 12px; font-weight: 600; margin-bottom: 8px;">
          <span style="font-size: 10px;">★</span> ${rating}
        </div>
      `
    }
    
    return `
      <div style="text-align: center; padding: 4px; min-width: 140px;">
        ${imageHtml}
        <div style="font-weight: 600; font-size: 13px; color: #111827; margin-bottom: 2px; line-height: 1.3;">
          ${bottle.name} ${year}
        </div>
        ${bottle.producer ? `<div style="font-size: 11px; color: #6b7280; margin-bottom: 6px;">${bottle.producer}</div>` : ""}
        ${ratingHtml}
        <a href="${bottle.url}" class="bottle-link" style="display: inline-block; padding: 6px 12px; background: #2563eb; color: white; font-size: 11px; font-weight: 500; border-radius: 6px; text-decoration: none; margin-top: 4px;">
          View Details →
        </a>
      </div>
    `
  }

  /**
   * Returns a color based on the rating value.
   *
   * @param {number|null} rating - The rating (0-5)
   * @returns {string} Hex color code
   */
  getRatingColor(rating) {
    if (!rating) return "#9ca3af" // gray
    if (rating < 2) return "#ef4444" // red
    if (rating < 3) return "#f97316" // orange
    if (rating < 4) return "#eab308" // yellow
    if (rating < 4.5) return "#84cc16" // lime
    return "#22c55e" // green
  }

  /**
   * Shows an empty state when no bottles have coordinates.
   */
  showEmptyState() {
    this.containerTarget.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <span class="material-symbols-outlined text-4xl text-gray-400 mb-2">location_off</span>
        <p class="text-gray-600 dark:text-gray-400">
          No bottles with location data yet.
        </p>
        <p class="text-sm text-gray-500 dark:text-gray-500 mt-1">
          Bottles will appear here once they have country/region information.
        </p>
      </div>
    `
  }
}

